import * as React from "react";
import { Drawer, DrawerBody, Button, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, Text } from "@fluentui/react-components";
import { useTranslation } from 'react-i18next';
import NavButton from "../NavButton";
import DrawerHeader from "../DrawerHeader";
import { useSettings, Chat } from "../../../contexts/SettingsContext";
import { Chat24Regular, Add24Regular, MoreHorizontal24Regular, Delete24Regular, Edit24Regular } from "@fluentui/react-icons";
import ConfirmDialog from "../../../components/UI/ConfirmDialog/ConfirmDialog";
import RenameChatDialog from "../../../components/UI/RenameChatDialog/RenameChatDialog";
import ChatNavButton from "../ChatNavButton";
import ScrollableContainer from "../../../components/UI/ScrollableContainer/ScrollableContainer";

interface NavigationDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  logo: string;
  title: string;
  currentPageType: PageType;
  navConfig: NavigationItem[];
  onNavigate: (path: string) => void;
}

export interface NavigationItem {
  id: PageType;
  labelKey: string;
  icon: React.ReactElement;
  path: string;
}

export type PageType = 'home' | 'settings' | 'info';

const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
  isOpen,
  onOpenChange,
  logo,
  title,
  currentPageType,
  navConfig,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const { settings, updateSettings } = useSettings();

  // Local dialog state for rename/delete actions
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [targetChat, setTargetChat] = React.useState<Chat | null>(null);

  const chats = settings.chats || [];
  const currentChatId = settings.currentChatId;

  const createNewChat = () => {
    // Do not persist empty chat. Prepare for a new chat session; first message will create it.
    // Also clear legacy single-chat messages so HomePage shows empty state.
    updateSettings({ currentChatId: undefined, messages: [] as any });
    onNavigate('/');
    onOpenChange(false);
  };

  const switchChat = (id: string) => {
    updateSettings({ currentChatId: id });
  };

  const openRenameDialog = (chat: Chat) => {
    setTargetChat(chat);
    setRenameOpen(true);
  };

  const confirmRename = (newName: string) => {
    if (!targetChat) return;
    const currChats = settings.chats || [];
    const updated = currChats.map(c => c.id === targetChat.id ? { ...c, name: newName, updatedAt: Date.now() } : c);
    updateSettings({ chats: updated });
    setRenameOpen(false);
    setTargetChat(null);
  };

  const cancelRename = () => {
    setRenameOpen(false);
    setTargetChat(null);
  };

  const openDeleteDialog = (chat: Chat) => {
    setTargetChat(chat);
    setDeleteOpen(true);
  };

  const confirmDelete = () => {
    if (!targetChat) return;
    const currChats = settings.chats || [];
    const remaining = currChats.filter(c => c.id !== targetChat.id);
    let nextCurrentId: string | undefined = (settings.currentChatId === targetChat.id) ? undefined : settings.currentChatId;
    if (!nextCurrentId && remaining.length > 0) {
      nextCurrentId = remaining.sort((a, b) => b.updatedAt - a.updatedAt)[0].id;
    }
    updateSettings({ chats: remaining, currentChatId: nextCurrentId });
    setDeleteOpen(false);
    setTargetChat(null);
    onNavigate('/');
    onOpenChange(false);
  };

  const cancelDelete = () => {
    setDeleteOpen(false);
    setTargetChat(null);
  };

  return (
    <Drawer
      type="overlay"
      separator
      position="start"
      open={isOpen}
      onOpenChange={(_, { open }) => onOpenChange(open)}
      size="small"
    >
      <DrawerHeader
        logo={logo}
        title={title}
        onClose={() => onOpenChange(false)}
      />

      <DrawerBody>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%'
        }}>
          {/* Top: Chat List + Main Navigation */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minHeight: 0 }}>
            {/* Chat header with New button */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <NavButton
                  icon={<Add24Regular />}
                  label={t('chat.newChat', 'New chat')}
                  showBadge={false}
                  onClick={() => createNewChat()}
                />
            </div>
            {/* Chat items (scrollable) */}
            <ScrollableContainer>
              {chats.length === 0 ? (
                <Text size={200} italic>{t('chat.noChats', 'No chats yet')}</Text>
              ) : (
                chats
                  .sort((a, b) => b.updatedAt - a.updatedAt)
                  .map(chat => (
                    <ChatNavButton
                      key={chat.id}
                      chat={chat}
                      label={chat.name || t('chat.newChat', 'New chat')}
                      isActive={currentChatId === chat.id && currentPageType === 'home'}
                      onClick={() => {
                        switchChat(chat.id);
                        onNavigate('/');
                        onOpenChange(false);
                      }}
                      onRename={openRenameDialog}
                      onDelete={openDeleteDialog}
                      showBadge={false}
                    />
                  ))
              )}
            </ScrollableContainer>

            </div>

          {/* Bottom: Settings and other bottom items */}
          {navConfig.length > 0 && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              marginTop: 'auto', 
              paddingBottom: '20px',
              width: '100%'
            }}>
              {navConfig.map((item) => (
                <NavButton
                  key={item.id}
                  icon={item.icon}
                  label={t(item.labelKey)}
                  isActive={currentPageType === item.id}
                  onClick={() => onNavigate(item.path)}
                />
              ))}
            </div>
          )}
        </div>
      </DrawerBody>

      <RenameChatDialog
        open={renameOpen}
        title={t('chat.rename', 'Rename')}
        initialValue={targetChat?.name || ''}
        confirmText={t('buttons.save', 'Save')}
        cancelText={t('buttons.cancel', 'Cancel')}
        onConfirm={confirmRename}
        onCancel={cancelRename}
      />

      <ConfirmDialog
        open={deleteOpen}
        title={t('chat.delete', 'Delete')}
        message={t('chat.deleteConfirm', 'Delete this chat?')}
        confirmText={t('buttons.delete', 'Delete')}
        cancelText={t('buttons.cancel', 'Cancel')}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

    </Drawer>
  );
};

export default NavigationDrawer;